import { ReparoApi } from './index';

// ─── Shared types ────────────────────────────────────────────────────────────

export type TransferStatus =
    | 'PLANNED'
    | 'DISPATCHED'
    | 'IN_TRANSIT'
    | 'PARTIALLY_RECEIVED'
    | 'RECEIVED'
    | 'DELAYED'
    | 'CANCELLED';

export type RebalancePlanStatus = 'DRAFT' | 'APPROVED' | 'EXECUTING' | 'COMPLETED' | 'ARCHIVED';

export type WarehouseHealthStatus = 'SURPLUS' | 'DEFICIT' | 'OK';

// ─── Geo nodes ────────────────────────────────────────────────────────────────

/** What the optimizer treats as a node: every warehouse, geo clusters, or one cluster's members. */
export type RebalanceScopeLevel = 'WAREHOUSE' | 'NETWORK' | 'NODE';
export type SourceSplitPolicy = 'RANK' | 'AVAILABILITY';
export type DestFillPolicy = 'RANK' | 'LARGEST_DEFICIT';
export type CapacityUnit = 'UNITS' | 'CBM';

export interface RebalanceScope {
    level: RebalanceScopeLevel;
    nodeId?: string | null;
    nodeName?: string | null;
}

export interface RebalanceNodeMember {
    warehouseId: string;
    warehouseName: string | null;
    sourceRank: number;
    destRank: number;
    keepFloorDoh: number | null;
}

export interface RebalanceNode {
    id: string;
    merchantId: string;
    name: string;
    targetDoh: number | null;
    sourceSplitPolicy: SourceSplitPolicy | null;
    destFillPolicy: DestFillPolicy | null;
    isActive: boolean;
    members: RebalanceNodeMember[];
    createdAt: string;
    updatedAt: string;
}

export interface RebalanceNodesResponse {
    nodes: RebalanceNode[];
    unassignedWarehouses: { warehouseId: string; name: string }[];
}

export interface UpsertRebalanceNodeMember {
    warehouseId: string;
    sourceRank?: number;
    destRank?: number;
    keepFloorDoh?: number | null;
}

export interface UpsertRebalanceNode {
    id?: string;
    name: string;
    targetDoh?: number | null;
    sourceSplitPolicy?: SourceSplitPolicy | null;
    destFillPolicy?: DestFillPolicy | null;
    isActive?: boolean;
    members: UpsertRebalanceNodeMember[];
}

/** One physical warehouse-to-warehouse shipment realising part of a node transfer. */
export interface TransferLeg {
    legId: string;
    pathId: string;
    fromWarehouseId: string;
    toWarehouseId: string;
    qty: number;
    leadTimeDays: number;
    costPerUnit: number | null;
}

export type UnallocatedReason = 'NO_LANE' | 'SOURCES_AT_FLOOR' | 'DESTINATIONS_FULL';

export interface RebalanceScoringWeights {
    deficitUrgency: number;
    costEfficiency: number;
    speed: number;
}

// ─── Path types ───────────────────────────────────────────────────────────────

export interface RebalanceWarehousePath {
    id: string;
    merchantId: string;
    fromWarehouseId: string | null;
    toWarehouseId: string;
    leadTimeDays: number | null;
    costPerUnit: number | null;
    targetDoh: number | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface UpsertPathEntry {
    fromWarehouseId: string | null;
    toWarehouseId: string;
    leadTimeDays?: number | null;
    costPerUnit?: number | null;
    targetDoh?: number | null;
    isActive?: boolean;
}

export interface UpdatePathBody {
    leadTimeDays?: number | null;
    costPerUnit?: number | null;
    targetDoh?: number | null;
    isActive?: boolean;
}

// ─── Config types ─────────────────────────────────────────────────────────────

export interface RebalanceMerchantConfig {
    id: string;
    merchantId: string;
    scoringWeights: RebalanceScoringWeights;
    warehousePriorityOrder: string[] | null;
    defaultTargetDoh: number;
    inTransitBufferDays: number;
    defaultSourceSplitPolicy: SourceSplitPolicy;
    defaultDestFillPolicy: DestFillPolicy;
    nodePriorityOrder: string[] | null;
    capacityUnit: CapacityUnit | null;
    createdAt: string;
    updatedAt: string;
}

// ─── Compute / Plan types ─────────────────────────────────────────────────────

export interface ProductHealthSnapshot {
    warehouseId: string;
    productId: string;
    currentStockUnits: number;
    inTransitUnits: number;
    targetDemandUnits: number;
    productName: string | null;
    skuCode: string | null;
    category: string | null;
    tier: string | null;
    productGroup: string | null;
    isActive: boolean | null;
}

export interface WarehouseHealthSnapshot {
    warehouseId: string;
    warehouseName?: string;
    currentStockUnits: number;
    inTransitUnits?: number;
    /** In-production (viaPo) into this WH within the horizon; incoming = in-transit + in-production. */
    inProductionUnits?: number;
    incomingUnits?: number;
    avgDailyDemand: number;
    currentDoh: number;
    targetDoh: number;
    status: WarehouseHealthStatus;
    surplusDeficitDays: number;
    nodeId?: string | null;
    nodeName?: string | null;
}

/** One optimizer node in a NETWORK run: a geo node folded over its members, or a singleton warehouse. */
export interface NodeHealthSnapshot extends WarehouseHealthSnapshot {
    nodeKey: string;
    isSingleton: boolean;
    memberWarehouseIds: string[];
}

export interface RecommendedTransfer {
    id: string;
    fromWarehouseId: string;
    toWarehouseId: string;
    productId: string;
    pathId: string;
    qtyRecommended: number;
    qtyApproved: number | null;
    leadTimeDays: number;
    costPerUnit: number | null;
    estimatedTotalCost: number | null;
    priorityScore: number;
    fromDohBefore: number;
    fromDohAfter: number;
    toDohBefore: number;
    toDohAfter: number;
    status: 'PENDING' | 'APPROVED' | 'MODIFIED' | 'REJECTED';
    skuCode?: string | null;
    productName?: string | null;
    category?: string | null;
    tier?: string | null;
    productGroup?: string | null;
    isActive?: boolean | null;
    /** Per-unit volume in CBM, used to total transfer volume. */
    volumeCbm?: number | null;
    // Node fields (NETWORK plans). fromWarehouseId/toWarehouseId hold the node id then.
    fromNodeId?: string | null;
    fromNodeName?: string | null;
    toNodeId?: string | null;
    toNodeName?: string | null;
    legs?: TransferLeg[];
    suggestedLegs?: TransferLeg[];
    legsEdited?: boolean;
    unallocatedQty?: number;
    unallocatedReason?: UnallocatedReason | null;
    capacityCappedQty?: number;
    /** True once approval has raised this transfer's STN(s); it is read-only from then on. */
    raised?: boolean;
}

export interface RebalanceComputeRequest {
    inTransitBufferDays?: number;
    warehouseIds?: string[];
    productIds?: string[];
    categories?: string[];
    targetDohOverride?: number;
    scope?: { level: RebalanceScopeLevel; nodeId?: string };
}

export interface RebalanceResult {
    warehouseHealth: WarehouseHealthSnapshot[];
    nodeHealth: NodeHealthSnapshot[] | null;
    capacityCappedUnitsByDestination?: Record<string, number>;
    productHealth: ProductHealthSnapshot[];
    recommendedTransfers: RecommendedTransfer[];
    totalRecommendedUnits: number;
    totalEstimatedCost: number | null;
    deficitWarehousesResolved: number;
    totalDeficitWarehouses: number;
    parameters: {
        inTransitBufferDays: number;
        defaultTargetDoh: number;
        scope?: RebalanceScope;
        capacityUnit?: CapacityUnit | null;
    };
}

export interface RebalancePlan {
    id: string;
    merchantId: string;
    name: string;
    planDate: string;
    status: RebalancePlanStatus;
    parameters: RebalanceResult['parameters'] & {
        warehouseIds: string[] | null;
        productIds: string[] | null;
        categories: string[] | null;
    };
    warehouseHealth: WarehouseHealthSnapshot[] | null;
    nodeHealth: NodeHealthSnapshot[] | null;
    productHealth: ProductHealthSnapshot[] | null;
    recommendedTransfers: RecommendedTransfer[] | null;
    aiNarrative: string | null;
    stnIds: string[] | null;
    createdAt: string;
    updatedAt: string;
}

export interface RebalancePlanTransfer {
    id: string;
    merchantId: string;
    rebalancePlanId: string | null;
    pathId: string | null;
    fromWarehouseId: string;
    toWarehouseId: string;
    productId: string;
    qtyPlanned: number;
    qtyDispatched: number | null;
    qtyReceived: number | null;
    status: TransferStatus;
    plannedDispatchDate: string | null;
    actualDispatchDate: string | null;
    expectedArrivalDate: string | null;
    actualArrivalDate: string | null;
    fromNodeId: string | null;
    fromNodeName?: string | null;
    toNodeId: string | null;
    toNodeName?: string | null;
    nodeTransferId: string | null;
    /** The STN raised for this leg on approval. Status and gate pass fields below are read from it. */
    stnId: string | null;
    stnCode?: string | null;
    stnStatus?: string | null;
    wmsGatePassCode?: string | null;
    wmsPushedAt?: string | null;
    externalStatus?: string | null;
    /** Outcome of the last push attempt: CREATED | FAILED. Null = never pushed. */
    wmsStatus: string | null;
    wmsErrorMessage: string | null;
    createdAt: string;
    updatedAt: string;
}

// ─── API functions ────────────────────────────────────────────────────────────

export const getRebalancePaths = async (): Promise<RebalanceWarehousePath[]> =>
    ReparoApi({ method: 'GET', url: '/rebalance/paths', body: null });

export const upsertRebalancePaths = async (
    paths: UpsertPathEntry[],
): Promise<RebalanceWarehousePath[]> =>
    ReparoApi({ method: 'PUT', url: '/rebalance/paths', body: JSON.stringify({ paths }) });

export const updateRebalancePath = async (
    id: string,
    body: UpdatePathBody,
): Promise<RebalanceWarehousePath> =>
    ReparoApi({ method: 'PATCH', url: `/rebalance/paths/${id}`, body: JSON.stringify(body) });

/** Adds one route. The server answers 409 when the lane (from → to) already exists. */
export const createRebalancePath = async (body: UpsertPathEntry): Promise<RebalanceWarehousePath> =>
    ReparoApi({ method: 'POST', url: '/rebalance/paths', body: JSON.stringify(body) });

/** Hard-deletes a route (or a warehouse DoH override row) — removed from the database, not deactivated. */
export const deleteRebalancePath = async (id: string): Promise<void> =>
    ReparoApi({ method: 'DELETE', url: `/rebalance/paths/${id}`, body: null });

export const getRebalanceConfig = async (): Promise<RebalanceMerchantConfig | null> =>
    ReparoApi({ method: 'GET', url: '/rebalance/config', body: null });

export const upsertRebalanceConfig = async (body: {
    scoringWeights: RebalanceScoringWeights;
    warehousePriorityOrder?: string[] | null;
    defaultTargetDoh?: number;
    inTransitBufferDays?: number;
    defaultSourceSplitPolicy?: SourceSplitPolicy;
    defaultDestFillPolicy?: DestFillPolicy;
    nodePriorityOrder?: string[] | null;
    capacityUnit?: CapacityUnit | null;
}): Promise<RebalanceMerchantConfig> =>
    ReparoApi({ method: 'PUT', url: '/rebalance/config', body: JSON.stringify(body) });

export interface WarehouseHealthResponse {
    warehouses: WarehouseHealthSnapshot[];
    /** Geo nodes folded over members plus singleton warehouses. Empty when the merchant has no nodes. */
    nodes: NodeHealthSnapshot[];
    summary: { surplus: number; deficit: number; balanced: number; total: number };
    computedAt: string;
}

export const getRebalanceNodes = async (): Promise<RebalanceNodesResponse> =>
    ReparoApi({ method: 'GET', url: '/rebalance/nodes', body: null });

/** Full replace of nodes and membership. Nodes left out are deleted; lanes are untouched. */
export const upsertRebalanceNodes = async (nodes: UpsertRebalanceNode[]): Promise<RebalanceNodesResponse> =>
    ReparoApi({ method: 'PUT', url: '/rebalance/nodes', body: JSON.stringify({ nodes }) });

export const deleteRebalanceNode = async (id: string): Promise<void> =>
    ReparoApi({ method: 'DELETE', url: `/rebalance/nodes/${id}`, body: null });

export const getRebalanceHealth = async (): Promise<WarehouseHealthResponse> =>
    ReparoApi({ method: 'GET', url: '/rebalance/health', body: null });

/** Preview only — runs the optimizer without saving. Plans are created via createRebalancePlan. */
export const computeRebalancePlan = async (
    params: RebalanceComputeRequest,
): Promise<RebalanceResult> =>
    ReparoApi({ method: 'POST', url: '/rebalance/compute', body: JSON.stringify(params) });

/**
 * Create a DRAFT plan. Only the compute INPUTS go over the wire; the backend runs the
 * optimizer and freezes the result into the plan (the result is far too large to round-trip
 * through the browser — it 413'd once).
 */
export const createRebalancePlan = async (body: {
    name: string;
    compute?: RebalanceComputeRequest;
}): Promise<RebalancePlan> =>
    ReparoApi({ method: 'POST', url: '/rebalance/plans', body: JSON.stringify(body) });

export const getRebalancePlans = async (
    limit = 20,
    offset = 0,
): Promise<{ data: RebalancePlan[]; total: number }> =>
    ReparoApi({ method: 'GET', url: `/rebalance/plans?limit=${limit}&offset=${offset}`, body: null });

export const getRebalancePlan = async (id: string): Promise<RebalancePlan> =>
    ReparoApi({ method: 'GET', url: `/rebalance/plans/${id}`, body: null });

export const updateRebalancePlan = async (
    id: string,
    body: {
        name?: string;
        /** Re-run the optimizer server-side; omitted fields fall back to the plan's stored scope/filters. `{}` = same inputs. */
        recompute?: RebalanceComputeRequest;
        transferQtyEdits?: Record<string, number>;
        transferLegEdits?: Record<string, TransferLeg[]>;
        transferLegResets?: string[];
        manualTransfers?: { fromWarehouseId: string; toWarehouseId: string; productId: string; qty: number }[];
    },
): Promise<RebalancePlan> =>
    ReparoApi({ method: 'PATCH', url: `/rebalance/plans/${id}`, body: JSON.stringify(body) });

export const deleteRebalancePlan = async (id: string): Promise<void> =>
    ReparoApi({ method: 'DELETE', url: `/rebalance/plans/${id}`, body: null });

export const approveRebalancePlan = async (
    id: string,
    body: { approvedTransferIds: string[]; qtyOverrides?: Record<string, number>; pushToWms?: boolean },
): Promise<RebalancePlan> =>
    ReparoApi({ method: 'POST', url: `/rebalance/plans/${id}/approve`, body: JSON.stringify(body) });

export interface ApprovalPreviewLine {
    productId: string;
    skuCode: string | null;
    productName: string | null;
    qty: number;
    cbm: number | null;
}

export interface ApprovalPreviewRoute {
    lines: ApprovalPreviewLine[];
    fromWarehouseId: string;
    fromWarehouseName: string;
    fromNodeName: string | null;
    toWarehouseId: string;
    toWarehouseName: string;
    toNodeName: string | null;
    skuCount: number;
    units: number;
    cbm: number | null;
    gatePassPossible: boolean;
    gatePassBlocker: string | null;
    fromFacilityCode: string | null;
    toFacilityCode: string | null;
}

export interface ApprovalPreview {
    transferCount: number;
    stnCount: number;
    totalUnits: number;
    wmsPushAvailable: boolean;
    wmsPushBlocker: string | null;
    routes: ApprovalPreviewRoute[];
}

/** What approval will raise: one STN (and gate pass, when possible) per warehouse pair. */
export const getRebalanceApprovalPreview = async (id: string, approvedTransferIds?: string[]): Promise<ApprovalPreview> =>
    ReparoApi({ method: 'POST', url: `/rebalance/plans/${id}/approval-preview`, body: JSON.stringify({ approvedTransferIds }) });

/** Push every un-pushed leg STN of an approved plan as a Uniware STOCK_TRANSFER gate pass; failed legs are retried. */
export const pushRebalancePlanToWms = async (id: string): Promise<RebalancePlanTransfer[]> =>
    ReparoApi({ method: 'POST', url: `/rebalance/plans/${id}/push-to-wms`, body: JSON.stringify({}) });

export const getRebalanceTransfers = async (params: {
    planId?: string;
    status?: TransferStatus;
}): Promise<RebalancePlanTransfer[]> => {
    const qs = new URLSearchParams();
    if (params.planId) qs.set('planId', params.planId);
    if (params.status) qs.set('status', params.status);
    return ReparoApi({ method: 'GET', url: `/rebalance/transfers?${qs.toString()}`, body: null });
};

export const updateRebalanceTransfer = async (
    id: string,
    body: {
        status?: TransferStatus;
        qtyDispatched?: number;
        qtyReceived?: number;
        actualDispatchDate?: string;
        actualArrivalDate?: string;
    },
): Promise<RebalancePlanTransfer> =>
    ReparoApi({ method: 'PATCH', url: `/rebalance/transfers/${id}`, body: JSON.stringify(body) });

// ─── Warehouse Demand types ───────────────────────────────────────────────────

export interface WarehouseDemandColumn {
    warehouseId: string;
    warehouseName: string;
}

export interface WarehouseDemandRow {
    channelId: string;
    channelName: string;
    periodStart: string;
    periodLabel: string;
    totalDemand: number;
    warehouseDemand: Record<string, number>;
    warehouseMixPct: Record<string, number>;
    hasOverride: boolean;
}

export interface WarehouseDemandTableResponse {
    columns: WarehouseDemandColumn[];
    rows: WarehouseDemandRow[];
    periodType: string;
}

export interface WarehouseDemandSkuRow {
    productId: string;
    skuCode: string;
    productName: string;
    totalDemand: number;
    warehouseDemand: Record<string, number>;
    warehouseMixPct: Record<string, number>;
    hasOverride: boolean;
}

export interface WarehouseDemandSkuTableResponse {
    channelId: string;
    channelName: string;
    periodStart: string;
    periodLabel: string;
    periodType: string;
    columns: WarehouseDemandColumn[];
    rows: WarehouseDemandSkuRow[];
}

export interface WhMixOverrideRow {
    productId: string;
    warehouseId: string;
    /** Absolute demand units for this WH × SKU × channel × period. */
    value: number;
}

// ─── Warehouse Demand API ─────────────────────────────────────────────────────

export const getWarehouseDemandTable = async (body: {
    channelId?: string;
    periodStart?: string;
}): Promise<WarehouseDemandTableResponse> =>
    ReparoApi({ method: 'POST', url: '/rebalance/warehouse-demand/table', body: JSON.stringify(body) });

export const getWarehouseDemandSkuTable = async (body: {
    channelId: string;
    periodStart: string;
}): Promise<WarehouseDemandSkuTableResponse> =>
    ReparoApi({ method: 'POST', url: '/rebalance/warehouse-demand/sku-table', body: JSON.stringify(body) });

export interface ForecastChange {
    productId: string;
    channelId: string;
    period: string;
    oldTotal: number | null;
    newTotal: number;
}

export const upsertWhMixOverrides = async (body: {
    channelId: string;
    periodStart: string;
    periodType: string;
    rows: WhMixOverrideRow[];
}): Promise<{ forecastChanges: ForecastChange[] }> =>
    ReparoApi({ method: 'PUT', url: '/rebalance/warehouse-demand/overrides', body: JSON.stringify(body) });

export const deleteWhMixOverrides = async (body: {
    channelId: string;
    periodStart: string;
    productId?: string;
}): Promise<void> =>
    ReparoApi({ method: 'DELETE', url: '/rebalance/warehouse-demand/overrides', body: JSON.stringify(body) });

export const addSkuDemand = async (body: {
    channelId: string;
    periodStart: string;
    periodType: string;
    skuCode: string;
    warehouseDemand: Record<string, number>;
}): Promise<void> =>
    ReparoApi({ method: 'POST', url: '/rebalance/warehouse-demand/add-sku', body: JSON.stringify(body) });
